import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FileText, Package, AlertCircle } from 'lucide-react';

import {
  CustomerQuoteHistoryItem,
  CustomerOrderHistoryItem,
} from '../../services/types';
import { parseCalendarDate } from '../../utils/calendarDate';

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const date = parseCalendarDate(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-CA');
}

function formatCurrency(value: number, currency: string): string {
  const formatted = value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${formatted} ${currency}` : formatted;
}

type StatusBadge = { label: string; className: string };

interface HistoryTableProps<T> {
  variant: 'quote' | 'order';
  items: T[];
  loading: boolean;
  error: string | null;
}

/**
 * Shared history table for a customer's quotes or orders. Each row's number
 * links to the corresponding Quote Tracker / Order Tracker page.
 */
export function CustomerHistoryTable<
  T extends CustomerQuoteHistoryItem | CustomerOrderHistoryItem,
>({
  variant,
  items,
  loading,
  error,
}: HistoryTableProps<T>): React.ReactElement {
  const { t } = useTranslation();
  const isQuote = variant === 'quote';

  const title = isQuote
    ? t('customerDashboard.quoteHistory.title')
    : t('customerDashboard.orderHistory.title');
  const numberHeader = isQuote
    ? t('customerDashboard.quoteHistory.quoteNum')
    : t('customerDashboard.orderHistory.orderNum');
  const dateHeader = isQuote
    ? t('customerDashboard.quoteHistory.dateQuoted')
    : t('customerDashboard.orderHistory.dateOrdered');
  const Icon = isQuote ? FileText : Package;

  const getStatusBadge = (item: T): StatusBadge => {
    if (isQuote) {
      const q = item as CustomerQuoteHistoryItem;
      return q.quoteClosed
        ? {
            label: t('customerDashboard.status.closed'),
            className: 'bg-blue-100 text-blue-800',
          }
        : {
            label: t('customerDashboard.status.open'),
            className: 'bg-green-100 text-green-800',
          };
    }
    const o = item as CustomerOrderHistoryItem;
    if (o.voidOrder) {
      return {
        label: t('customerDashboard.status.void'),
        className: 'bg-gray-100 text-gray-800',
      };
    }
    return o.openOrder
      ? {
          label: t('customerDashboard.status.open'),
          className: 'bg-green-100 text-green-800',
        }
      : {
          label: t('customerDashboard.status.closed'),
          className: 'bg-blue-100 text-blue-800',
        };
  };

  const total = items.reduce(
    (sum, item) =>
      sum +
      (isQuote
        ? (item as CustomerQuoteHistoryItem).docTotalQuote
        : (item as CustomerOrderHistoryItem).docOrderAmt),
    0
  );
  const currency = items.length > 0 ? items[0].currencyCode : '';

  return (
    <div className="bg-white rounded border border-gray-300 shadow-sm">
      {/* Header band */}
      <div className="bg-blue-50 px-4 py-3 border-b border-blue-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-blue-800" />
          <h2 className="text-lg font-bold text-blue-900">
            {title} ({items.length})
          </h2>
        </div>
        {items.length > 0 && (
          <p className="text-xs text-blue-700 font-medium">
            {formatCurrency(total, currency)}
          </p>
        )}
      </div>

      {loading ? (
        <div className="p-4 bg-blue-50 border-b border-blue-100">
          <p className="text-sm text-blue-700">{t('common.loading')}...</p>
        </div>
      ) : error ? (
        <div className="p-4 bg-red-50 border-b border-red-100 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      ) : items.length === 0 ? (
        <div className="p-6 text-sm text-gray-500">
          {isQuote
            ? t('customerDashboard.quoteHistory.empty')
            : t('customerDashboard.orderHistory.empty')}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-tight">
                  {numberHeader}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-tight">
                  {dateHeader}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-tight">
                  {t('customerDashboard.shipTo')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-tight">
                  {t('customerDashboard.total')}
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-tight">
                  {t('customerDashboard.statusLabel')}
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const badge = getStatusBadge(item);
                const num = isQuote
                  ? (item as CustomerQuoteHistoryItem).quoteNum
                  : (item as CustomerOrderHistoryItem).orderNum;
                const date = isQuote
                  ? (item as CustomerQuoteHistoryItem).dateQuoted
                  : (item as CustomerOrderHistoryItem).orderDate;
                const amount = isQuote
                  ? (item as CustomerQuoteHistoryItem).docTotalQuote
                  : (item as CustomerOrderHistoryItem).docOrderAmt;
                const to = isQuote ? `/quotes/${num}` : `/sales-orders/${num}`;
                return (
                  <tr
                    key={num}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Link
                        to={to}
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {num}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {formatDate(date)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {item.shipToName || item.shipToNum || '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900 font-medium whitespace-nowrap">
                      {formatCurrency(amount, item.currencyCode)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-medium ${badge.className}`}
                      >
                        {badge.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
